import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Search, Plus, Eye, RefreshCw, ReceiptText, CheckCircle2, Clock, XCircle, FileCheck } from 'lucide-react';
import axios from 'axios';
import DataTable from '../components/common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusBadge = (status) => {
  const badges = {
    'CREATED': { color: 'bg-orange-100 text-orange-800', icon: Clock },
    'RELEASED': { color: 'bg-yellow-100 text-yellow-800', icon: FileCheck },
    'POSTED': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    'CANCELLED': { color: 'bg-red-100 text-red-800', icon: XCircle }
  };
  const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', icon: ReceiptText };
  const Icon = badge.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const CreditMemoList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [memos, setMemos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({ customer_id: '', status: '' });

  useEffect(() => {
    loadCustomers();
    loadMemos();
  // eslint-disable-next-line
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customers`);
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadMemos = async () => {
    setIsLoading(true);
    try {
      let url = `${BACKEND_URL}/api/otc/credit-memos`;
      const params = new URLSearchParams();
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.status) params.append('status', filters.status);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url);
      setMemos(response.data || []);
    } catch (error) {
      console.error('Error loading credit memos:', error);
      toast.error('Failed to load credit memos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => loadMemos();

  const columns = [
    { key: 'credit_memo_number', header: 'Credit Memo #', filterable: true, accessor: (m) => m.credit_memo_number,
      render: (m) => (
        <button onClick={() => navigate(`/otc/credit-memos/${m.credit_memo_number}`)} className="font-mono font-medium text-orange-600 hover:underline">
          {m.credit_memo_number}
        </button>
      ) },
    { key: 'billing_type', header: 'Type',
      render: (m) => <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded">{m.billing_type}</span> },
    { key: 'customer_id', header: 'Customer', filterable: true, accessor: (m) => m.customer_id },
    { key: 'credit_date', header: 'Credit Date', accessor: (m) => m.credit_date },
    { key: 'reference', header: 'Reference',
      render: (m) => (
        m.reference_returns_number ? (
          <button onClick={() => navigate(`/otc/returns/${m.reference_returns_number}`)} className="font-mono text-sm text-orange-600 hover:underline">{m.reference_returns_number}</button>
        ) : m.reference_billing_number ? (
          <button onClick={() => navigate(`/otc/billing/${m.reference_billing_number}`)} className="font-mono text-sm text-orange-600 hover:underline">{m.reference_billing_number}</button>
        ) : <span className="text-gray-400">Manual</span>
      ) },
    { key: 'net_value', header: 'Net Value', align: 'right',
      render: (m) => <span className="font-mono">${m.net_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> },
    { key: 'gross_value', header: 'Gross Value', align: 'right',
      render: (m) => <span className="font-mono font-semibold text-green-600">${m.gross_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> },
    { key: 'status', header: 'Status', align: 'center', render: (m) => getStatusBadge(m.status) },
    { key: 'actions', header: 'Actions', align: 'center',
      render: (m) => (
        <Button variant="ghost" size="sm" onClick={() => navigate(`/otc/credit-memos/${m.credit_memo_number}`)} data-testid={`view-memo-${m.credit_memo_number}`}>
          <Eye className="w-4 h-4" />
        </Button>
      ) },
  ];

  return (
    <Layout>
      <div className="p-6" data-testid="credit-memo-list">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Credit Memos</h1>
            <p className="text-sm text-gray-600">Manage customer credit adjustments</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadMemos} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {canWrite('credit_memo') && (
            <Button onClick={() => navigate('/otc/credit-memos/create')} data-testid="create-credit-memo-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Credit Memo
            </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
              <Select value={filters.customer_id || '__ALL__'} onValueChange={(v) => setFilters({...filters, customer_id: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>{c.customer_id} - {c.customer_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <Select value={filters.status || '__ALL__'} onValueChange={(v) => setFilters({...filters, status: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Statuses</SelectItem>
                  <SelectItem value="CREATED">Created</SelectItem>
                  <SelectItem value="RELEASED">Released</SelectItem>
                  <SelectItem value="POSTED">Posted</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={handleSearch} className="w-full">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="p-6">
          <DataTable
            testid="credit-memo-table"
            columns={columns}
            data={memos}
            loading={isLoading}
            rowKey={(m) => m.credit_memo_number}
            emptyText="No credit memos found"
            emptyHint="Credit memos are created from approved returns orders."
          />
        </Card>
      </div>
    </Layout>
  );
};

export default CreditMemoList;
